/**
 * Hook for managing market data with automatic caching via Zustand store.
 * Integrates InfoClient with the centralized market store.
 */

import { useCallback, useEffect, useRef } from "react";
import { InfoClient } from "../sdk";
import { useMarketStore } from "../store";
import type {
  Market,
  OrderbookData,
  TickerData,
  RecentTrade,
} from "../store/types";
import type {
  Market as ApiMarket,
  RawTrade,
  OrderbookEntry,
} from "../sdk/types/api";

export interface UseMarketsConfig {
  baseURL: string;
  sessionToken?: string;
  /** When true, fetches markets list on mount if cache is empty. */
  autoFetchMarkets?: boolean;
}

// --- Transformers (API → store) ---

/** Optional fields that may appear in API market responses. */
type ApiMarketOptional = ApiMarket & {
  moniker?: string;
  baseAsset?: string;
  quoteAsset?: string;
  maxSizeLimit?: number;
  minSizeLimit?: number;
};

function toStoreMarket(apiMarket: ApiMarketOptional): Market {
  const active = apiMarket.status === "MARKET_STATUS_ACTIVE";
  return {
    ...apiMarket,
    index: apiMarket.marketIndex,
    active,
    maxSizeLimit: apiMarket.maxSizeLimit ?? 0,
    minSizeLimit: apiMarket.minSizeLimit ?? 0,
    moniker: apiMarket.moniker ?? apiMarket.symbol ?? "",
    baseAsset: apiMarket.baseAsset ?? "",
    quoteAsset: apiMarket.quoteAsset ?? "",
  };
}

function transformRawTradeToRecentTrade(
  rawTrade: RawTrade,
  marketId: string,
): RecentTrade {
  const timestamp =
    typeof rawTrade.timestamp === "string"
      ? parseInt(rawTrade.timestamp, 10) || Date.now()
      : typeof rawTrade.timestamp === "number"
        ? rawTrade.timestamp
        : Date.now();
  const side: "buy" | "sell" =
    rawTrade.tradeType?.toLowerCase().includes("buy") || rawTrade.buyOrderId
      ? "buy"
      : rawTrade.tradeType?.toLowerCase().includes("sell") ||
          rawTrade.sellOrderId
        ? "sell"
        : "buy";
  return {
    ...rawTrade,
    id: rawTrade.tradeId,
    marketId: rawTrade.marketIndex || marketId,
    price: rawTrade.price,
    size: rawTrade.quantity,
    side,
    timestamp,
  };
}

/** Convert API orderbook entries to [number, number][]. */
function convertOrderbookEntries(
  entries: OrderbookEntry[],
): [number, number][] {
  return entries.map(([price, size]) => [
    typeof price === "string" ? parseFloat(price) : price,
    typeof size === "string" ? parseFloat(size) : size,
  ]);
}

/** Symbol: moniker > symbol > baseAsset{sep}quoteAsset. Default sep '-' for display; use '/' for API-style. */
function getMarketSymbol(
  market: {
    moniker?: string | null;
    symbol?: string | null;
    baseAsset?: string | null;
    quoteAsset?: string | null;
  },
  pairSeparator: string = "-",
): string | null {
  if (market.moniker) return market.moniker;
  if (market.symbol) return market.symbol;
  if (market.baseAsset && market.quoteAsset)
    return `${market.baseAsset}${pairSeparator}${market.quoteAsset}`;
  return null;
}

/** Extract marketId from loading key like "orderbook_1" or "ticker_1". */
function getMarketIdFromLoadingKey(key: string, prefix: string): string | null {
  if (!key.startsWith(prefix)) return null;
  const rest = key.slice(prefix.length);
  return rest || null;
}

/**
 * Hook for centralized market data management
 *
 * @example
 * ```tsx
 * const { markets, getMarket, fetchMarkets, fetchOrderbook } = useMarkets({
 *   baseURL: 'https://router.morpheum.io',
 *   autoFetchMarkets: true,
 * });
 * ```
 */
export function useMarkets(config: UseMarketsConfig) {
  const infoClientRef = useRef<InfoClient | null>(null);

  // Store — data
  const markets = useMarketStore((state) => state.markets);
  const marketsById = useMarketStore((state) => state.marketsById);
  const orderbooks = useMarketStore((state) => state.orderbooks);
  const tickers = useMarketStore((state) => state.tickers);
  const recentTrades = useMarketStore((state) => state.recentTrades);
  const selectedMarketId = useMarketStore((state) => state.selectedMarketId);
  // Store — loading & errors
  const loadingMarkets = useMarketStore((state) => state.loadingMarkets);
  const loadingOrderbooks = useMarketStore((state) => state.loadingOrderbooks);
  const loadingTickers = useMarketStore((state) => state.loadingTickers);
  const loadingTrades = useMarketStore((state) => state.loadingTrades);
  const errors = useMarketStore((state) => state.errors);
  // Store — actions
  const setMarkets = useMarketStore((state) => state.setMarkets);
  const setMarket = useMarketStore((state) => state.setMarket);
  const getMarket = useMarketStore((state) => state.getMarket);
  const getMarketBySymbol = useMarketStore((state) => state.getMarketBySymbol);
  const setSelectedMarketId = useMarketStore(
    (state) => state.setSelectedMarketId,
  );
  const setOrderbook = useMarketStore((state) => state.setOrderbook);
  const getOrderbook = useMarketStore((state) => state.getOrderbook);
  const updateOrderbook = useMarketStore((state) => state.updateOrderbook);
  const setTicker = useMarketStore((state) => state.setTicker);
  const getTicker = useMarketStore((state) => state.getTicker);
  const setRecentTrades = useMarketStore((state) => state.setRecentTrades);
  const getRecentTrades = useMarketStore((state) => state.getRecentTrades);
  const setLoading = useMarketStore((state) => state.setLoading);
  const setError = useMarketStore((state) => state.setError);
  const clearError = useMarketStore((state) => state.clearError);
  const clearCache = useMarketStore((state) => state.clearCache);

  const getInfoClient = useCallback((): InfoClient => {
    const client = infoClientRef.current;
    if (!client) throw new Error("InfoClient not initialized");
    return client;
  }, []);

  async function runWithLoading<T>(
    key: string,
    fallbackError: string,
    fn: () => Promise<T>,
  ): Promise<T> {
    setLoading(key, true);
    setError(key, null);
    try {
      return await fn();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : fallbackError;
      setError(key, message);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }

  useEffect(() => {
    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({
        baseURL: config.baseURL,
        sessionToken: config.sessionToken,
      });
    } else if (config.sessionToken) {
      infoClientRef.current.setSessionToken(config.sessionToken);
    }
  }, [config.baseURL, config.sessionToken]);

  const fetchMarkets = useCallback(async (): Promise<Market[]> => {
    const client = getInfoClient();
    return runWithLoading("markets", "Failed to fetch markets", async () => {
      const data = await client.getMarkets();
      const marketsList: Market[] = (data.markets ?? []).map(toStoreMarket);
      setMarkets(marketsList);
      return marketsList;
    });
  }, [getInfoClient, setMarkets]);

  const fetchMarket = useCallback(
    async (marketId: string): Promise<Market | null> => {
      const cached = getMarket(marketId);
      if (cached) return cached;
      const client = getInfoClient();
      try {
        const apiMarket = await client.getMarketByID(marketId);
        if (apiMarket) {
          const market = toStoreMarket(apiMarket);
          setMarket(market);
          return market;
        }
        return null;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : `Failed to fetch market ${marketId}`;
        setError(`market_${marketId}`, message);
        throw error;
      }
    },
    [getMarket, getInfoClient, setMarket, setError],
  );

  const fetchMarketBySymbol = useCallback(
    async (symbol: string): Promise<Market | null> => {
      const cached = getMarketBySymbol(symbol);
      if (cached) return cached;
      const client = getInfoClient();
      try {
        let apiMarket: ApiMarket | undefined;
        try {
          apiMarket = await client.getMarketByTicker(symbol);
        } catch {
          apiMarket = await client.getMarketByMoniker(symbol);
        }
        if (apiMarket) {
          const market = toStoreMarket(apiMarket);
          setMarket(market);
          return market;
        }
        return null;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : `Failed to fetch market ${symbol}`;
        setError(`market_${symbol}`, message);
        throw error;
      }
    },
    [getMarketBySymbol, getInfoClient, setMarket, setError],
  );

  const fetchOrderbook = useCallback(
    async (
      marketId: string,
      convertToDecimal: boolean = true,
      depth: number = 20,
    ): Promise<OrderbookData | null> => {
      const client = getInfoClient();
      const key = `orderbook_${marketId}`;
      return runWithLoading(
        key,
        `Failed to fetch orderbook for market ${marketId}`,
        async () => {
          const data = await client.getOrderbook(
            marketId,
            convertToDecimal,
            depth,
          );
          if (data && (data.bids || data.asks)) {
            const orderbook: OrderbookData = {
              bids: convertOrderbookEntries(data.bids || []),
              asks: convertOrderbookEntries(data.asks || []),
              timestamp: Date.now(),
            };
            setOrderbook(marketId, orderbook);
            return orderbook;
          }
          return null;
        },
      );
    },
    [getInfoClient, setOrderbook],
  );

  const fetchTicker = useCallback(
    async (marketId: string): Promise<TickerData | null> => {
      const client = getInfoClient();
      const key = `ticker_${marketId}`;
      return runWithLoading(
        key,
        `Failed to fetch ticker for market ${marketId}`,
        async () => {
          const data = await client.getMarketStats(marketId);
          if (data) {
            const ticker: TickerData = {
              marketId,
              ...data,
              timestamp: Date.now(),
            };
            setTicker(marketId, ticker);
            return ticker;
          }
          return null;
        },
      );
    },
    [getInfoClient, setTicker],
  );

  const fetchRecentTrades = useCallback(
    async (
      marketId: string,
      limit: number = 100,
    ): Promise<RecentTrade[] | null> => {
      const client = getInfoClient();
      const key = `trades_${marketId}`;
      return runWithLoading(
        key,
        `Failed to fetch trades for market ${marketId}`,
        async () => {
          const data = await client.getMarketTrades(marketId, limit);
          if (data) {
            const rawTrades = Array.isArray(data) ? data : [];
            const trades: RecentTrade[] = rawTrades.map((raw) =>
              transformRawTradeToRecentTrade(raw, marketId),
            );
            setRecentTrades(marketId, trades);
            return trades;
          }
          return null;
        },
      );
    },
    [getInfoClient, setRecentTrades],
  );

  // Auto-fetch markets on mount if enabled
  useEffect(() => {
    if (config.autoFetchMarkets && markets.length === 0 && !loadingMarkets) {
      fetchMarkets().catch(console.error);
    }
  }, [config.autoFetchMarkets, markets.length, loadingMarkets, fetchMarkets]);

  const selectedMarket = selectedMarketId
    ? markets.find(
        (m) => m.index === selectedMarketId || m.marketIndex === selectedMarketId,
      )
    : null;
  const selectedMarketSymbol = selectedMarket
    ? getMarketSymbol(selectedMarket, "-")
    : null;

  const getSymbolByMarketId = useCallback(
    (marketId: string): string | null => {
      const market = markets.find(
        (m) => m.index === marketId || m.marketIndex === marketId,
      );
      return market ? getMarketSymbol(market, "/") : null;
    },
    [markets],
  );

  return {
    // Data
    markets,
    marketsById,
    orderbooks,
    tickers,
    recentTrades,
    selectedMarketId,
    selectedMarket,
    selectedMarketSymbol,

    // Loading states
    loadingMarkets,
    loadingOrderbooks,
    loadingTickers,
    loadingTrades,
    isLoading: (key: string) => {
      if (key === "markets") return loadingMarkets;
      const orderbookId = getMarketIdFromLoadingKey(key, "orderbook_");
      if (orderbookId != null) return loadingOrderbooks.has(orderbookId);
      const tickerId = getMarketIdFromLoadingKey(key, "ticker_");
      if (tickerId != null) return loadingTickers.has(tickerId);
      const tradesId = getMarketIdFromLoadingKey(key, "trades_");
      if (tradesId != null) return loadingTrades.has(tradesId);
      return false;
    },

    // Errors
    errors,
    getError: (key: string) => errors.get(key),

    // Getters (from cache)
    getMarket,
    getMarketBySymbol,
    getSymbolByMarketId,
    getOrderbook,
    getTicker,
    getRecentTrades,

    // Fetchers (from API)
    fetchMarkets,
    fetchMarket,
    fetchMarketBySymbol,
    fetchOrderbook,
    fetchTicker,
    fetchRecentTrades,

    // Store actions
    updateOrderbook,
    clearCache,
    clearError,
    setSelectedMarketId,

    /** Current InfoClient instance; throws if not yet initialized. */
    getInfoClient,
  };
}
